import React, { useContext } from "react";
import jsonData from "../data/dataNews.json";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { LanguageContext } from "../LanguageContext";
import "../css/singleNews.css";

const SingleNews = () => {
  const { language = 'sr' } = useContext(LanguageContext) || {};
  const newsText = {
    sr: {
      back: 'Nazad na novosti',
      notFound: 'Vesti nisu pronađene!',
    },
    en: {
      back: 'Back to news',
      notFound: 'News not found!',
    },
  };
  const currentLanguage = language === 'en' ? 'en' : 'sr';
  const { id } = useParams();

  const getTranslatedField = (item, field) => item[`${field}_${currentLanguage}`] || item[field] || "";

  const newsItem = jsonData.find((item) => item.id === id);

  if (!newsItem) {
    return <div className="single-news-container">{newsText[currentLanguage].notFound}</div>;
  }

  // Collect all description fields
  const descriptions = [
    getTranslatedField(newsItem, 'opis1'),
    getTranslatedField(newsItem, 'opis2'),
    getTranslatedField(newsItem, 'opis3'),
    getTranslatedField(newsItem, 'opis4'),
    getTranslatedField(newsItem, 'opis5'),
    getTranslatedField(newsItem, 'opis6'),
    getTranslatedField(newsItem, 'opis7'),
    getTranslatedField(newsItem, 'opis8'),
    getTranslatedField(newsItem, 'opis9'),
    getTranslatedField(newsItem, 'opis10'),
    getTranslatedField(newsItem, 'opis11'),
    getTranslatedField(newsItem, 'opis12'),
    getTranslatedField(newsItem, 'opis13')
  ].filter(Boolean);

  const makeHtml = (text) => {
    if (!text) return '';
    // If HTML anchor already present, return as-is
    if (/<a\s+href=/i.test(text)) return text;
    const urlRegex = /(https?:\/\/[^\s"']+)/g;
    return text.replace(urlRegex, (url) => {
      try {
        const host = new URL(url).hostname;
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${host}</a>`;
      } catch (e) {
        return `<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`;
      }
    });
  };

  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    try {
      const parsedUrl = new URL(url);
      const hostname = parsedUrl.hostname.toLowerCase();
      let videoId = null;

      if (hostname.includes('youtu.be')) {
        videoId = parsedUrl.pathname.slice(1);
      } else if (hostname.includes('youtube.com')) {
        videoId = parsedUrl.searchParams.get('v') || parsedUrl.pathname.split('/').pop();
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    } catch (e) {
      return null;
    }
  };

  // Support `video` being either a string or an array in the JSON data
  const rawVideoField = getTranslatedField(newsItem, 'video');
  const videoUrls = Array.isArray(rawVideoField)
    ? rawVideoField
    : rawVideoField
    ? [rawVideoField]
    : [];

  // Separate local MP4 videos from YouTube videos
  const youtubeUrls = videoUrls
    .map((u) => ({ url: u, embed: getYouTubeEmbedUrl(u) }))
    .filter((v) => v.embed !== null);
  
  const localVideoUrls = videoUrls.filter((u) => u.includes('.mp4'));

  const galleryImages = [
    ...(newsItem.slike || []),
    ...(newsItem.gallery || []),
  ];

  return (
    <div className="single-news-container">
      <Link to="/news" className="single-news-back-link">
        <FontAwesomeIcon icon={faChevronLeft} />
        {newsText[language].back}
      </Link>

      <div className="single-news-header">
        <h1 className="single-news-title">{getTranslatedField(newsItem, 'naziv')}</h1>
        <p className="single-news-date">{newsItem.datum4}</p>
      </div>

      <div className="single-news-content">
        {descriptions.map((description, index) => (
          <p
            key={index}
            className="single-news-text"
            dangerouslySetInnerHTML={{ __html: makeHtml(description) }}
          />
        ))}

        {(youtubeUrls.length > 0 || localVideoUrls.length > 0) && (
          <div className="single-news-video">
            {youtubeUrls.map((item, idx) => (
              <div key={`yt-${idx}`} className="single-news-video-wrapper" style={{ marginBottom: youtubeUrls.length + localVideoUrls.length > 1 ? 20 : 0 }}>
                <iframe
                  title={`News video ${newsItem.id}-${idx}`}
                  src={item.embed}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ))}
            {localVideoUrls.map((videoUrl, idx) => (
              <div key={`local-${idx}`} className="single-news-video-wrapper" style={{ marginBottom: idx < localVideoUrls.length - 1 ? 20 : 0 }}>
                <video
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  controls
                  controlsList="nodownload"
                  title={`Local video ${newsItem.id}-${idx}`}
                >
                  <source src={videoUrl} type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            ))}
          </div>
        )}

        {galleryImages.length > 0 && (
          <div className="single-news-gallery">
            {galleryImages.map((image, index) => (
              <div key={index} className="single-news-gallery-item">
                <a href={image} target="_blank" rel="noopener noreferrer">
                  <img src={image} alt={`${newsItem.naziv} ${index + 1}`} />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SingleNews;
